import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  History, Search, Filter, RefreshCw, User, Calendar, FileText,
  Plus, Edit, Trash2, ChevronLeft, ChevronRight, BarChart3, Clock,
  Users, BedDouble, Pill, FlaskConical, Receipt, Activity, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AuditHistory() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('logs');
  
  // Filters
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [entityFilter, actionFilter, userFilter, dateFrom, dateTo, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (userFilter !== 'all') params.append('user_id', userFilter);
      if (dateFrom) params.append('start_date', new Date(dateFrom).toISOString());
      if (dateTo) params.append('end_date', new Date(dateTo + 'T23:59:59').toISOString());
      params.append('limit', limit);
      params.append('skip', page * limit);
      
      const [logsRes, countRes] = await Promise.all([
        api.get(`/audit-logs?${params.toString()}`),
        api.get(`/audit-logs/count?${params.toString()}`)
      ]);
      
      setLogs(logsRes.data);
      setTotalCount(countRes.data.count);
      
      // Fetch stats and users for admin
      if (isAdmin) {
        const [statsRes, usersRes] = await Promise.all([
          api.get('/audit-logs/stats?days=30'),
          api.get('/audit-logs/users')
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="h-4 w-4 text-emerald-600" />;
      case 'update': return <Edit className="h-4 w-4 text-amber-600" />;
      case 'delete': return <Trash2 className="h-4 w-4 text-rose-600" />;
      default: return <Activity className="h-4 w-4 text-slate-600" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-emerald-100 text-emerald-700';
      case 'update': return 'bg-amber-100 text-amber-700';
      case 'delete': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'patient': return <Users className="h-4 w-4" />;
      case 'appointment': return <Calendar className="h-4 w-4" />;
      case 'admission': return <BedDouble className="h-4 w-4" />;
      case 'prescription': return <Pill className="h-4 w-4" />;
      case 'lab_entry': return <FlaskConical className="h-4 w-4" />;
      case 'medical_record': return <FileText className="h-4 w-4" />;
      case 'billing': return <Receipt className="h-4 w-4" />;
      case 'visit': return <Activity className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEntityType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (userFilter !== 'all') params.append('user_id', userFilter);
      if (dateFrom) params.append('start_date', new Date(dateFrom).toISOString());
      if (dateTo) params.append('end_date', new Date(dateTo + 'T23:59:59').toISOString());
      
      const response = await api.get(`/audit-logs/export/${format}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Audit logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.user_name?.toLowerCase().includes(search) ||
      log.entity_name?.toLowerCase().includes(search) ||
      log.entity_type?.toLowerCase().includes(search) ||
      log.details?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Audit History
            </h1>
            <p className="text-slate-600 mt-2">Track all data modifications made by users</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleExport('csv')}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport('json')}
                  className="gap-2"
                  data-testid="export-json-btn"
                >
                  <Download className="h-4 w-4" />
                  Export JSON
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={fetchData}
              className="gap-2"
              data-testid="refresh-audit-logs"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs for Admin */}
        {isAdmin && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 h-auto">
              <TabsTrigger 
                value="logs" 
                data-testid="logs-tab"
                className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
              >
                <History className="h-4 w-4 mr-2" />
                Audit Logs
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                data-testid="analytics-tab"
                className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Analytics Tab (Admin Only) */}
        {isAdmin && activeTab === 'analytics' && stats && (
          <div className="space-y-6 mb-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Activity className="h-5 w-5 text-[#4A238F]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_actions}</p>
                      <p className="text-sm text-slate-600">Total Actions (30 days)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Plus className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.by_action?.create || 0}</p>
                      <p className="text-sm text-slate-600">Created</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Edit className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.by_action?.update || 0}</p>
                      <p className="text-sm text-slate-600">Updated</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center">
                      <Trash2 className="h-5 w-5 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.by_action?.delete || 0}</p>
                      <p className="text-sm text-slate-600">Deleted</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Activity by Entity Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Activity by Entity Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_entity || {}).sort((a, b) => b[1] - a[1]).map(([entity, count]) => (
                      <div key={entity} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getEntityIcon(entity)}
                          <span className="text-slate-700">{formatEntityType(entity)}</span>
                        </div>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Top Active Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.top_users?.map((u, index) => (
                      <div key={u.user_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-amber-100 text-amber-700' :
                            index === 1 ? 'bg-slate-200 text-slate-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {index + 1}
                          </div>
                          <span className="text-slate-700">{u.user_name}</span>
                        </div>
                        <Badge variant="outline">{u.count} actions</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {(activeTab === 'logs' || !isAdmin) && (
          <>
            {/* Filters */}
            <Card className="border border-slate-200 mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-sm text-slate-600 mb-1 block">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search by user, entity, details..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="audit-search-input"
                      />
                    </div>
                  </div>
                  
                  <div className="w-[150px]">
                    <Label className="text-sm text-slate-600 mb-1 block">Entity Type</Label>
                    <Select value={entityFilter} onValueChange={setEntityFilter}>
                      <SelectTrigger data-testid="entity-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="patient">Patient</SelectItem>
                        <SelectItem value="appointment">Appointment</SelectItem>
                        <SelectItem value="visit">Visit</SelectItem>
                        <SelectItem value="prescription">Prescription</SelectItem>
                        <SelectItem value="lab_entry">Lab Entry</SelectItem>
                        <SelectItem value="medical_record">Medical Record</SelectItem>
                        <SelectItem value="admission">Admission</SelectItem>
                        <SelectItem value="discharge_summary">Discharge Summary</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-[120px]">
                    <Label className="text-sm text-slate-600 mb-1 block">Action</Label>
                    <Select value={actionFilter} onValueChange={setActionFilter}>
                      <SelectTrigger data-testid="action-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="create">Create</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                        <SelectItem value="delete">Delete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {isAdmin && users.length > 0 && (
                    <div className="w-[180px]">
                      <Label className="text-sm text-slate-600 mb-1 block">User</Label>
                      <Select value={userFilter} onValueChange={setUserFilter}>
                        <SelectTrigger data-testid="user-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {users.map((u) => (
                            <SelectItem key={u.user_id} value={u.user_id}>
                              {u.user_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="w-[140px]">
                    <Label className="text-sm text-slate-600 mb-1 block">From Date</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      data-testid="date-from-input"
                    />
                  </div>
                  
                  <div className="w-[140px]">
                    <Label className="text-sm text-slate-600 mb-1 block">To Date</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      data-testid="date-to-input"
                    />
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEntityFilter('all');
                      setActionFilter('all');
                      setUserFilter('all');
                      setDateFrom('');
                      setDateTo('');
                      setSearchTerm('');
                      setPage(0);
                    }}
                    data-testid="clear-filters"
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Logs List */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-[#4A238F]" />
                    Audit Logs
                  </CardTitle>
                  <Badge variant="outline">{totalCount} total records</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-[#4A238F] mx-auto mb-2" />
                    <p className="text-slate-600">Loading audit logs...</p>
                  </div>
                ) : filteredLogs.length === 0 ? (
                  <div className="p-12 text-center">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No audit logs found</p>
                    <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredLogs.map((log) => (
                      <motion.div
                        key={log.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedLog(log)}
                        data-testid={`audit-log-${log.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getActionColor(log.action)}`}>
                            {getActionIcon(log.action)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-900">{log.user_name}</span>
                              <Badge variant="outline" className="text-xs capitalize">{log.user_role}</Badge>
                              <span className="text-slate-600">
                                {log.action === 'create' ? 'created' : log.action === 'update' ? 'updated' : 'deleted'}
                              </span>
                              <span className="text-slate-600">a</span>
                              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                {getEntityIcon(log.entity_type)}
                                <span className="ml-1">{formatEntityType(log.entity_type)}</span>
                              </Badge>
                            </div>
                            
                            {log.entity_name && (
                              <p className="text-sm text-slate-600 mb-1">
                                <span className="font-medium">{log.entity_name}</span>
                                {log.details && <span className="text-slate-500"> - {log.details}</span>}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Clock className="h-3 w-3" />
                              <span>{formatTimestamp(log.timestamp)}</span>
                              {log.entity_id && (
                                <>
                                  <span className="text-slate-300">•</span>
                                  <span>ID: {log.entity_id.slice(0, 8)}...</span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {log.changes && (
                            <Badge variant="outline" className="text-xs">
                              {Object.keys(log.changes.new || {}).length} changes
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} of {totalCount}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600">
                        Page {page + 1} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        data-testid="next-page"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Log Detail Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-[#4A238F]" />
                Audit Log Details
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                {/* Action Summary */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getActionColor(selectedLog.action)}`}>
                      {getActionIcon(selectedLog.action)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {selectedLog.user_name} {selectedLog.action === 'create' ? 'created' : selectedLog.action === 'update' ? 'updated' : 'deleted'} a {formatEntityType(selectedLog.entity_type)}
                      </p>
                      <p className="text-sm text-slate-600">{formatTimestamp(selectedLog.timestamp)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">User</p>
                    <p className="text-slate-900">{selectedLog.user_name}</p>
                    <Badge variant="outline" className="mt-1 capitalize">{selectedLog.user_role}</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Entity Type</p>
                    <div className="flex items-center gap-2">
                      {getEntityIcon(selectedLog.entity_type)}
                      <span className="text-slate-900">{formatEntityType(selectedLog.entity_type)}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Entity ID</p>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{selectedLog.entity_id}</code>
                  </div>
                  {selectedLog.entity_name && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">Entity Name</p>
                      <p className="text-slate-900">{selectedLog.entity_name}</p>
                    </div>
                  )}
                </div>
                
                {/* Details */}
                {selectedLog.details && (
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Details</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedLog.details}</p>
                  </div>
                )}
                
                {/* Changes (for updates) */}
                {selectedLog.changes && (selectedLog.changes.old || selectedLog.changes.new) && (
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2">Changes Made</p>
                    <div className="bg-slate-50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left p-3 font-medium text-slate-600">Field</th>
                            <th className="text-left p-3 font-medium text-slate-600">Old Value</th>
                            <th className="text-left p-3 font-medium text-slate-600">New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys({...(selectedLog.changes.old || {}), ...(selectedLog.changes.new || {})}).map((field) => (
                            <tr key={field} className="border-b border-slate-100 last:border-0">
                              <td className="p-3 font-medium text-slate-700">{field}</td>
                              <td className="p-3 text-rose-600">
                                {selectedLog.changes.old?.[field] !== undefined 
                                  ? String(selectedLog.changes.old[field])
                                  : '-'}
                              </td>
                              <td className="p-3 text-emerald-600">
                                {selectedLog.changes.new?.[field] !== undefined
                                  ? String(selectedLog.changes.new[field])
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* IP Address */}
                {selectedLog.ip_address && (
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">IP Address</p>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{selectedLog.ip_address}</code>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
